// Composite the scene in fp16 instead of the 8-bit canvas drawing buffer.
//
// The splat pass sums dozens of near-transparent layers per pixel. An 8-bit
// buffer rounds AND clamps every one of those blends, and the error compounds
// into a magenta cast with banding, worst where depth complexity is highest.
// WebGL can't give the CANVAS a float drawing buffer, so: take over the render
// loop, draw into a half-float target, and blit that to the canvas — the 8-bit
// step then happens once, at present, instead of once per blend.
//
// Taking over the loop is what `useFrame(cb, 1)` means in R3F: a callback at
// priority > 0 disables the automatic render and becomes responsible for
// presenting. Nothing else in this app registers a positive priority.
//
// The `isXRRenderTarget` flag on the target is load-bearing — see makeTarget().

import * as THREE from 'three'
import { useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'

// Raw passthrough: this shader never includes <colorspace_fragment> or
// <tonemapping_fragment>, so the target's values reach the canvas unmodified
// (modulo the single final 8-bit round). Alpha is carried through rather than
// forced to 1 — this canvas is transparent over a CSS background.
const BLIT_VERT = `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`
const BLIT_FRAG = `
uniform sampler2D tSrc;
varying vec2 vUv;
void main() {
  gl_FragColor = texture2D(tSrc, vUv);
}
`

function makeTarget(width, height, samples, colorSpace) {
  const rt = new THREE.WebGLRenderTarget(Math.max(1, width), Math.max(1, height), {
    format: THREE.RGBAFormat,
    type: THREE.HalfFloatType,
    // Mirrors the renderer's own output space. Because of the flag below this
    // becomes the authority for every material's output encoding, so it must
    // not drift from what the canvas would have used.
    colorSpace,
    // The scene has real geometry (avatar meshes, gizmos, shadow catchers), so
    // unlike a splats-only pass this target needs depth. No stencil is used.
    depthBuffer: true,
    stencilBuffer: false,
    // Preserve the canvas's MSAA. R3F requests antialias by default; without
    // this the move off the drawing buffer would silently drop it.
    samples,
  })

  // "This target IS the screen."
  //
  // three picks a material's output colour space and tone mapping from the
  // CURRENT render target: for a plain target it forces workingColorSpace and
  // NoToneMapping, assuming a later output pass applies both. That assumption
  // breaks here, because the scene is mixed:
  //
  //   - PBR materials would stop tone-mapping and stop encoding to sRGB.
  //   - Splats would NOT change. Spark's splat shader is fully custom and never
  //     #includes <colorspace_fragment> or <tonemapping_fragment>, so three's
  //     injected encode function is defined but never called. (It's a plain
  //     ShaderMaterial, so three DOES inject the prefix — the shader simply
  //     never uses it.)
  //
  // The two groups would then disagree about what space the buffer is in, and
  // no single blit could correct both. isXRRenderTarget is three's existing
  // "treat this target like the canvas" flag (verified against three 0.184,
  // which carries a removal TODO in WebXRManager — re-check on upgrade).
  //
  // Deliberately NOT the fully-linear alternative (encodeLinear on spark +
  // tone-map/encode in the blit). That would move the gaussian blend into
  // linear space, which is not what the models were fit against — 3DGS blends
  // the sRGB-encoded values as-is.
  rt.isXRRenderTarget = true
  return rt
}

/** Owns the target + blit and drives the present. Mounted only when supported. */
function Fp16Present() {
  const gl = useThree((s) => s.gl)
  const scene = useThree((s) => s.scene)
  const camera = useThree((s) => s.camera)
  const size = useThree((s) => s.size)
  const dpr = useThree((s) => s.viewport.dpr)

  // Everything GPU-side is created and destroyed as one unit, keyed on the
  // RENDERER. That pairing matters twice over: on a context loss/restore or an
  // R3F renderer swap, a target built against the dead renderer would otherwise
  // stay wired to the blit and present a permanently black canvas; and holding
  // these in a ref rather than a useMemo keeps create/dispose symmetric under
  // StrictMode's double-mount (a memo is not re-run for the second mount, so
  // disposing one in an effect cleanup leaves the remount using dead objects).
  const gpuRef = useRef(null)

  useEffect(() => {
    const buffer = gl.getDrawingBufferSize(new THREE.Vector2())
    const samples = gl.getContextAttributes?.()?.antialias ? 4 : 0
    const rt = makeTarget(buffer.x, buffer.y, samples, gl.outputColorSpace)

    const material = new THREE.ShaderMaterial({
      uniforms: { tSrc: { value: rt.texture } },
      vertexShader: BLIT_VERT,
      fragmentShader: BLIT_FRAG,
      depthTest: false,
      depthWrite: false,
      // The quad covers every pixel and we want its RGBA verbatim, not blended
      // against the freshly cleared canvas.
      blending: THREE.NoBlending,
    })
    const geometry = new THREE.PlaneGeometry(2, 2)
    const mesh = new THREE.Mesh(geometry, material)
    mesh.frustumCulled = false
    mesh.matrixAutoUpdate = false
    const blitScene = new THREE.Scene()
    blitScene.matrixWorldAutoUpdate = false
    blitScene.add(mesh)

    gpuRef.current = {
      rt,
      blitScene,
      blitCamera: new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1),
      material,
      geometry,
    }
    // Derived from the target, never hardcoded: this line is the only external
    // evidence of what the present path is actually accumulating into, so it
    // has to change when the target does.
    const format = rt.texture.type === THREE.HalfFloatType ? 'RGBA16F' : `type#${rt.texture.type}`
    console.info(`[render] fp16 blend accumulation (${format} target + blit, samples=${rt.samples})`)

    return () => {
      rt.dispose()
      material.dispose()
      geometry.dispose()
      gpuRef.current = null
    }
  }, [gl])

  useEffect(() => {
    const gpu = gpuRef.current
    if (!gpu) return
    const buffer = gl.getDrawingBufferSize(new THREE.Vector2())
    gpu.rt.setSize(Math.max(1, buffer.x), Math.max(1, buffer.y))
  }, [gl, size.width, size.height, dpr])

  useFrame(() => {
    const gpu = gpuRef.current
    if (!gpu) {
      // First frame, before the effect runs.
      gl.setRenderTarget(null)
      gl.render(scene, camera)
      return
    }
    gl.setRenderTarget(gpu.rt)
    gl.render(scene, camera)
    gl.setRenderTarget(null)
    gl.render(gpu.blitScene, gpu.blitCamera)
  }, 1)

  return null
}

/**
 * Capability gate. Renders the present override when a half-float target is
 * available; otherwise mounts nothing, so R3F keeps its own automatic render
 * and the app falls back to 8-bit accumulation. The stand-down is announced on
 * the console rather than silently taken.
 */
export default function Fp16BlendTarget() {
  const gl = useThree((s) => s.gl)

  // RGBA16F is colour-renderable under EITHER extension on WebGL2 — three asks
  // for EXT_color_buffer_float itself when it sees a half-float target.
  const supported = useMemo(() => {
    try {
      const ctx = gl.getContext?.()
      return (
        !!gl.capabilities?.isWebGL2 &&
        !!(ctx?.getExtension('EXT_color_buffer_half_float') || ctx?.getExtension('EXT_color_buffer_float'))
      )
    } catch {
      return false
    }
  }, [gl])

  useEffect(() => {
    if (!supported) {
      console.warn('[render] fp16 blend accumulation unavailable (no WebGL2 half-float target) — staying on 8-bit')
    }
  }, [supported])

  return supported ? <Fp16Present /> : null
}
